import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Send, Image } from 'lucide-react';
import { cn, escapeHtml } from '@/utils';
import { handleError, ValidationError } from '@/errors';
import { FILE_UPLOAD_CONFIG } from '@/config';
import { useImageState, useImageActions } from '@/contexts/ImageContext';

interface ChatInputProps {
  readonly onSendMessage: (message: string, files?: File[], isInpaintingMode?: boolean) => void;
  readonly disabled?: boolean;
  readonly onNewChat: () => void;
  readonly currentChatId?: number | null; // Add optional chatId prop
  readonly messages?: any[]; // Add messages prop to get original image
  readonly imageViewerMode?: boolean; // Add flag to indicate we're in image viewer mode
  readonly printScaleImage?: string | null; // Data URL for print scale overlay
}

export function ChatInput({ onSendMessage, disabled = false, onNewChat: _onNewChat, currentChatId: _currentChatId = null, messages = [], imageViewerMode = false, printScaleImage = null }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Get inpainting state and actions
  const { selectedImage, selectedImageMessageId, inpainting } = useImageState();
  const { clearInpainting, deselectImage } = useImageActions();

  // Helper function to convert canvas data URL to File
  const dataURLToFile = async (dataURL: string, filename: string, mimeType: string = 'image/png'): Promise<File> => {
    const response = await fetch(dataURL);
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  };

  // Helper function to convert image URL to File
  const imageURLToFile = async (imageUrl: string, filename: string): Promise<File> => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type || 'image/png' });
  };

  // Helper function to prepare inpainting files
  const prepareInpaintingFiles = async (): Promise<File[]> => {
    const inpaintingFiles: File[] = [];
    
    if (inpainting.isActive && selectedImage !== -1 && selectedImageMessageId) {
      try {
        // Find the selected message and get the original image
        const selectedMessage = messages.find(msg => msg.id === selectedImageMessageId);
        if (selectedMessage?.imageUrls?.[selectedImage]) {
          const originalImageUrl = selectedMessage.imageUrls[selectedImage];
          
          // Convert original image to File (no compression)
          const originalImageFile = await imageURLToFile(originalImageUrl, 'original_image.png');
          inpaintingFiles.push(originalImageFile);
          
          // If we have mask data, convert it to File (no compression)
          if (inpainting.maskData) {
            const maskFile = await dataURLToFile(inpainting.maskData, 'inpainting_mask.png', 'image/png');
            inpaintingFiles.push(maskFile);
          }
        }
      } catch (error) {
        await handleError(error, {
          setError,
          context: 'Failed to process inpainting data'
        });
      }
    }
    
    return inpaintingFiles;
  };

  // Helper function to prepare files for sending
  const prepareFilesToSend = async (): Promise<{ files: File[], isInpaintingMode: boolean }> => {
    let filesToSend = [...selectedFiles];
    let isInpaintingMode = false;
    // Check if we're in inpainting mode and prepare inpainting files
    if (inpainting.isActive && selectedImage !== -1 && selectedImageMessageId) {
      isInpaintingMode = true;
      const inpaintingFiles = await prepareInpaintingFiles();
      filesToSend = [...inpaintingFiles, ...selectedFiles];
    }
    // If we're in image viewer mode but not inpainting, include the current image and print scale image if present
    else if (imageViewerMode && selectedImage !== -1 && selectedImageMessageId) {
      const selectedMessage = messages.find(msg => msg.id === selectedImageMessageId);
      let extraFiles: File[] = [];
      if (selectedMessage?.imageUrls?.[selectedImage]) {
        const originalImageUrl = selectedMessage.imageUrls[selectedImage];
        const imageFile = await imageURLToFile(originalImageUrl, 'current_image.png');
        extraFiles.push(imageFile);
      }
      if (printScaleImage) {
        // Convert printScaleImage (data URL) to File
        const printFile = await dataURLToFile(printScaleImage, 'print_scale_image.png', 'image/png');
        extraFiles.push(printFile);
      }
      if (extraFiles.length > 0) {
        filesToSend = [...extraFiles, ...selectedFiles];
      }
    }
    return { files: filesToSend, isInpaintingMode };
  };

  // Helper function to clean up after message submission
  const cleanupAfterSubmit = (isInpaintingMode: boolean) => {
    // Clean up message input
    setMessage('');
    setSelectedFiles([]);
    setError(null);
    
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls([]);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Clean up inpainting state if we were in inpainting mode
    if (isInpaintingMode && inpainting.isActive) {
      clearInpainting();
      deselectImage();
    }
  };

  // Helper function to get placeholder text based on current state
  const getPlaceholderText = (): string => {
    return "Type your message here...";
  };

  const handleSubmit = async () => {
    if ((message.trim() || selectedFiles.length > 0 || imageViewerMode) && !disabled && !isProcessing) {
      setIsProcessing(true);
      
      try {
        const { files: filesToSend, isInpaintingMode } = await prepareFilesToSend();
        
        // Send message with files (base64 conversion now happens in useChatMessages)
        onSendMessage(message.trim() || "", filesToSend.length > 0 ? filesToSend : undefined, isInpaintingMode);
        
        cleanupAfterSubmit(isInpaintingMode);
      } catch (err) {
        await handleError(err, {
          setError,
          context: 'Processing message submission'
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const processFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const validationErrors: string[] = [];
    const supportedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.bmp'];
    
    try {
      // Check if adding these files would exceed the limit
      const remainingSlots = FILE_UPLOAD_CONFIG.MAX_FILES - selectedFiles.length;
      
      if (remainingSlots <= 0) {
        throw new ValidationError(
          FILE_UPLOAD_CONFIG.MESSAGES.TOO_MANY_FILES(FILE_UPLOAD_CONFIG.MAX_FILES),
          'fileValidation',
          'TOO_MANY_FILES'
        );
      }

      // Filter fileArray to only include files that can fit within the limit
      const filesToProcess = fileArray.slice(0, remainingSlots);
      
      // Show warning if some files will be ignored
      if (fileArray.length > remainingSlots) {
        validationErrors.push(
          `You can only add ${remainingSlots} more image(s). Maximum ${FILE_UPLOAD_CONFIG.MAX_FILES} images per message. ${fileArray.length - remainingSlots} file(s) will be ignored.`
        );
      }

      // Validate files and separate valid from invalid
      filesToProcess.forEach((file) => {
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        const hasSupportedMimeType = FILE_UPLOAD_CONFIG.SUPPORTED_TYPES.includes(fileType as any);
        const hasSupportedExtension = supportedExtensions.some(ext => fileName.endsWith(ext));

        // Check file type
        if (!hasSupportedMimeType && !hasSupportedExtension) {
          validationErrors.push(FILE_UPLOAD_CONFIG.MESSAGES.UNSUPPORTED_FORMAT(file.name));
          return; // Skip to next file
        }
        
        // Check file size
        if (file.size > FILE_UPLOAD_CONFIG.MAX_SIZE) {
          const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
          validationErrors.push(FILE_UPLOAD_CONFIG.MESSAGES.FILE_TOO_LARGE(file.name, fileSizeMB));
          return; // Skip to next file
        }
        
        // File passed all validations, add to valid files
        validFiles.push(file);
      });
      
      // If we have validation errors, show them all at once
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.length === 1 
          ? validationErrors[0]
          : `Multiple file errors:\n• ${validationErrors.join('\n• ')}`;
          
        throw new ValidationError(
          errorMessage,
          'fileValidation',
          'FILE_VALIDATION_ERRORS'
        );
      }
      
      // Process valid files if any
      if (validFiles.length > 0) {
        setError(null);
        setSelectedFiles(prev => [...prev, ...validFiles]);
        
        // Create preview URLs for the valid image files
        const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
        setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
      }
    } catch (err) {
      await handleError(err, {
        context: 'File validation'
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      processFiles(files);
    }
    
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getExtensionFromMimeType = (mimeType: string): string => {
    switch (mimeType.toLowerCase()) {
      case 'image/jpeg':
      case 'image/jpg':
        return 'jpg';
      case 'image/png':
        return 'png';
      case 'image/gif':
        return 'gif';
      case 'image/webp':
        return 'webp';
      case 'image/avif':
        return 'avif';
      case 'image/bmp':
      case 'image/x-ms-bmp':
        return 'bmp';
      default:
        return 'png';
    }
  };

  const blobToFile = async (blob: Blob, baseName: string): Promise<File> => {
    const extension = getExtensionFromMimeType(blob.type || 'image/png');
    const safeBaseName = baseName.replace(/\.[^/.]+$/, '');
    return new File([blob], `${safeBaseName}.${extension}`, {
      type: blob.type || 'image/png',
      lastModified: Date.now(),
    });
  };

  const extractImageUrlCandidates = (text: string): string[] => {
    const trimmed = text.trim();
    if (!trimmed) {
      return [];
    }

    const candidates: string[] = [];
    const addCandidate = (value?: string | null) => {
      const candidate = value?.trim();
      if (candidate && !candidates.includes(candidate)) {
        candidates.push(candidate);
      }
    };

    addCandidate(trimmed);

    if (trimmed.startsWith('<')) {
      const srcMatches = Array.from(trimmed.matchAll(/src=["']([^"']+)["']/gi));
      srcMatches.forEach((match) => addCandidate(match[1]));
    }

    return candidates;
  };

  const getStringItems = (dataTransfer: DataTransfer): DataTransferItem[] => {
    if (!dataTransfer.items || dataTransfer.items.length === 0) {
      return [];
    }

    return Array.from(dataTransfer.items).filter((item) => item.kind === 'string');
  };

  const getDataTransferItemValue = (item: DataTransferItem): Promise<string> => {
    return new Promise((resolve) => {
      item.getAsString((value) => {
        resolve(value ?? '');
      });
    });
  };

  const getFallbackStringPayloads = (dataTransfer: DataTransfer): string[] => {
    return [
      dataTransfer.getData('application/x-dpc-image-url'),
      dataTransfer.getData('text/uri-list'),
      dataTransfer.getData('text/plain'),
      dataTransfer.getData('text/html'),
    ];
  };

  const getFileNameFromCandidate = (candidate: string): string => {
    try {
      const url = new URL(candidate, globalThis.location.href);
      const rawName = url.pathname.split('/')
        .reverse()
        .find(Boolean);
      return rawName || 'dropped-image';
    } catch {
      return 'dropped-image';
    }
  };

  const resolveDroppedUrlsToFiles = async (e: React.DragEvent): Promise<File[]> => {
    const stringItems = getStringItems(e.dataTransfer);
    const itemPayloads = await Promise.all(stringItems.map(getDataTransferItemValue));
    const stringPayloads = itemPayloads.filter(Boolean);

    if (stringPayloads.length === 0) {
      stringPayloads.push(...getFallbackStringPayloads(e.dataTransfer));
    }

    const candidates = stringPayloads
      .flatMap(extractImageUrlCandidates)
      .filter((value, index, array) => array.indexOf(value) === index);

    const convertedFiles: File[] = [];

    for (const candidate of candidates) {
      try {
        const response = await fetch(candidate);
        const blob = await response.blob();

        if (!blob.type.startsWith('image/')) {
          continue;
        }

        const fileNameFromUrl = getFileNameFromCandidate(candidate);

        convertedFiles.push(await blobToFile(blob, fileNameFromUrl));
      } catch {
        // Try the next candidate; some string drops are not fetchable or are not image URLs.
      }
    }

    return convertedFiles;
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragOver to false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const extractDroppedFiles = (e: React.DragEvent): File[] => {
    const itemFiles: File[] = [];

    // Prefer DataTransfer.items for better cross-platform compatibility (notably macOS).
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      for (const item of Array.from(e.dataTransfer.items)) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            itemFiles.push(file);
          }
        }
      }
    }

    if (itemFiles.length > 0) {
      return itemFiles;
    }

    return e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) {
      return;
    }

    const droppedFiles = extractDroppedFiles(e);
    const droppedUrlFiles = droppedFiles.length === 0 ? await resolveDroppedUrlsToFiles(e) : [];
    const filesToProcess = droppedFiles.length > 0 ? droppedFiles : droppedUrlFiles;

    if (filesToProcess.length > 0) {
      await processFiles(filesToProcess);
      return;
    }

    await handleError(
      new ValidationError(
        'No image files were detected in this drop. Please drop an image file, an image from chat, or use the upload button.',
        'fileValidation',
        'INVALID_DROP_PAYLOAD'
      ),
      {
        context: 'Drop file validation'
      }
    );
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeFile = (index: number) => {
    // Clean up the preview URL before removing
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }
    
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isProcessing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Paste handler
useEffect(() => {
  const handlePaste = (e: ClipboardEvent) => {
    if (disabled) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      processFiles(imageFiles);
    }
  };

  document.addEventListener('paste', handlePaste);
  return () => document.removeEventListener('paste', handlePaste);
}, [disabled, processFiles]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [message]);

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  return (
    <div 
      ref={dropZoneRef}
      className={cn(
        "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 transition-colors relative",
        isDragOver && "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600"
      )}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-50/80 dark:bg-blue-900/40 backdrop-blur-sm rounded-lg border-2 border-dashed border-blue-400 dark:border-blue-500 z-10">
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">
              Drop images here
            </div>
            <div className="text-sm text-blue-500 dark:text-blue-300">
              Supports PNG, JPG, JPEG, GIF, WEBP, AVIF, BMP (max 5 MB, 4 images per message)
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto relative">
        {error && (
          <div
            role="alert"
            className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
          >
            {error}
          </div>
        )}

        {/* Selected files display with thumbnails */}
        {selectedFiles.length > 0 && (
          <div className="mb-3">
            {isProcessing && (
              <div className="mb-2 text-sm text-blue-600 dark:text-blue-400 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent mr-2"></div>
                Converting images to base64...
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${escapeHtml(file.name)}-${file.size}-${file.lastModified}`}
                  className="flex items-center bg-slate-300 dark:bg-slate-600 rounded-lg p-2 text-sm max-w-[250px]"
                >
                  {/* Thumbnail preview */}
                  <div className="w-12 h-12 mr-3 flex-shrink-0">
                    <img
                      src={previewUrls[index]?.startsWith('blob:') ? previewUrls[index] : ''}
                      alt={`Preview of ${escapeHtml(file.name)}`}
                      className="w-full h-full object-cover rounded border border-gray-300 dark:border-gray-600"
                    />
                  </div>
                  
                  {/* File info - simplified to just name */}
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium text-slate-800 dark:text-white">
                      {escapeHtml(file.name)}
                    </div>
                  </div>
                  
                  {/* Remove button */}
                  <button
                    onClick={() => removeFile(index)}
                    className="ml-2 text-slate-600 dark:text-gray-300 hover:text-red-500 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-slate-400 dark:hover:bg-slate-500 transition-colors"
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex items-center space-x-3">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={FILE_UPLOAD_CONFIG.ACCEPT_ATTRIBUTE}
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Unified input container with consistent background */}
          <div className="flex-1 relative flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 min-h-[44px]">
            
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (error) {
                  setError(null);
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholderText()}
              className={cn(
                'flex-1 resize-none overflow-hidden outline-none focus:outline-none bg-transparent',
                'placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              disabled={disabled}
              rows={1}
            />
            
            {/* Right side buttons */}
            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
              {/* File upload button */}
              <button
                onClick={handleFileUploadClick}
                disabled={disabled}
                className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-lg transition-colors",
                  "text-gray-600 dark:text-gray-400 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700",
                  "disabled:opacity-50"
                )}
                style={{ cursor: disabled ? 'default' : 'pointer' }}
                aria-label="Upload image"
              >
                <Image className="h-5 w-5" />
              </button>
              
              {/* Send button */}
              <button
                onClick={handleSubmit}
                disabled={disabled || isProcessing || (!message.trim() && selectedFiles.length === 0 && !(imageViewerMode && selectedImage !== -1))}
                className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-lg transition-colors",
                  "disabled:opacity-50",
                  (message.trim() || selectedFiles.length > 0 || (imageViewerMode && selectedImage !== -1)) && !disabled && !isProcessing
                    ? "text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer" 
                    : "text-gray-400 dark:text-gray-500"
                )}
                style={{ cursor: (message.trim() || selectedFiles.length > 0 || (imageViewerMode && selectedImage !== -1)) && !disabled && !isProcessing ? 'pointer' : 'default' }}
                aria-label={isProcessing ? "Processing images..." : "Send message"}
              >
                {isProcessing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

